import {ComponentProps} from 'react';
import styled, {css} from 'styled-components';

type FilterIconButtonProps = ComponentProps<'button'> & {
  active?: boolean;
  size?: number;
};

const Button = styled.button<{$active?: boolean; $size: number}>`
  display: grid;
  place-items: center;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid
    ${(p) =>
      p.$active ? p.theme.colors.violet300 : p.theme.colors.border};
  background: ${(p) =>
    p.$active ? p.theme.colors.secondary100 : p.theme.colors.neutral100};
  box-shadow: ${(p) => p.theme.elevation.xs};
  cursor: pointer;
  color: ${(p) =>
    p.$active ? p.theme.colors.violet600 : p.theme.colors.primary500};
  transition: transform ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out},
    background ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out},
    border-color ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out},
    box-shadow ${(p) => p.theme.motion.duration.fast}
      ${(p) => p.theme.motion.easing.out};

  &:hover {
    transform: translateY(-1px);
    background: ${(p) => p.theme.colors.secondary100};
    border-color: ${(p) => p.theme.colors.violet300};
    box-shadow: ${(p) => p.theme.elevation.sm};
  }

  &:active {
    transform: scale(0.96);
  }

  ${(p) =>
    p.$active &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 6px;
        right: 6px;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${p.theme.colors.violet600};
        border: 1.5px solid ${p.theme.colors.neutral100};
      }
    `}

  position: relative;

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:active {
      transform: none;
    }
  }
`;

/** Circular filter trigger matching the FIRE redesign language. */
export const FilterIconButton = ({
  active = false,
  size = 40,
  children,
  type = 'button',
  ...rest
}: FilterIconButtonProps) => {
  return (
    <Button $active={active} $size={size} type={type} {...rest}>
      {children ?? (
        <svg
          width="18"
          height="14"
          viewBox="0 0 24 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M0 16H8V13.3333H0V16ZM0 0V2.66667H24V0H0ZM0 9.33333H16V6.66667H0V9.33333Z"
            fill="currentColor"
          />
        </svg>
      )}
    </Button>
  );
};
